import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CustomProposalForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    companySize: '',
    industry: '',
    projectScope: '',
    projectTimeline: '',
    budget: '',
    specificRequirements: '',
    currentChallenges: '',
    desiredOutcomes: '',
    gdprConsent: false,
    marketingConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'sme', label: 'SME (51-250 employees)' },
    { value: 'enterprise', label: 'Enterprise (251-1000 employees)' },
    { value: 'large-enterprise', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'technology', label: 'Technology' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'other', label: 'Other' }
  ];

  const projectScopeOptions = [
    { value: 'data-strategy', label: 'Data Strategy Development' },
    { value: 'fractional-cdo', label: 'Fractional CDO Services' },
    { value: 'ai-implementation', label: 'AI Implementation' },
    { value: 'data-governance', label: 'Data Governance & Compliance' },
    { value: 'analytics-platform', label: 'Analytics Platform Implementation' },
    { value: 'data-quality', label: 'Data Quality Improvement' },
    { value: 'team-training', label: 'Team Training & Capability Building' },
    { value: 'comprehensive', label: 'Comprehensive Transformation' },
    { value: 'other', label: 'Other (Please specify)' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: 'short-term', label: 'Short-term (1-3 months)' },
    { value: 'medium-term', label: 'Medium-term (3-6 months)' },
    { value: 'long-term', label: 'Long-term (6+ months)' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const budgetOptions = [
    { value: 'under-25k', label: 'Under €25,000' },
    { value: '25k-50k', label: '€25,000 - €50,000' },
    { value: '50k-100k', label: '€50,000 - €100,000' },
    { value: '100k-250k', label: '€100,000 - €250,000' },
    { value: 'over-250k', label: 'Over €250,000' },
    { value: 'not-determined', label: 'Not yet determined' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: '',
        phone: '',
        companySize: '',
        industry: '',
        projectScope: '',
        projectTimeline: '',
        budget: '',
        specificRequirements: '',
        currentChallenges: '',
        desiredOutcomes: '',
        gdprConsent: false,
        marketingConsent: false
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <section id="proposal-form" className="py-20 bg-surface border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl shadow-elevation p-8 text-center transition-all duration-500 hover:shadow-2xl">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 hover:bg-success/20 hover:scale-110">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h3 className="text-2xl font-semibold text-primary mb-4">
              Proposal Request Submitted Successfully!
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Thank you for requesting a custom proposal. Our team will carefully review your requirements and prepare a detailed proposal tailored to your specific needs.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>What happens next:</strong>
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex flex-col items-center space-y-2 p-4 bg-surface rounded-lg transition-all duration-300 hover:bg-surface-hover hover:shadow-md hover:scale-105">
                  <Icon name="Clock" size={24} className="text-secondary transition-transform duration-300 hover:scale-110" />
                  <span className="font-medium">Within 24 Hours</span>
                  <span className="text-muted-foreground text-center">Initial acknowledgment & clarifying questions</span>
                </div>
                <div className="flex flex-col items-center space-y-2 p-4 bg-surface rounded-lg transition-all duration-300 hover:bg-surface-hover hover:shadow-md hover:scale-105">
                  <Icon name="FileText" size={24} className="text-secondary transition-transform duration-300 hover:scale-110" />
                  <span className="font-medium">2-3 Business Days</span>
                  <span className="text-muted-foreground text-center">Detailed proposal delivery</span>
                </div>
                <div className="flex flex-col items-center space-y-2 p-4 bg-surface rounded-lg transition-all duration-300 hover:bg-surface-hover hover:shadow-md hover:scale-105">
                  <Icon name="Users" size={24} className="text-secondary transition-transform duration-300 hover:scale-110" />
                  <span className="font-medium">Follow-up Call</span>
                  <span className="text-muted-foreground text-center">Proposal walkthrough & Q&A</span>
                </div>
              </div>
            </div>
            <div className="mt-8 p-4 bg-secondary/5 rounded-lg transition-all duration-300 hover:bg-secondary/10">
              <p className="text-sm text-muted-foreground mb-2 transition-all duration-300 hover:text-primary">
                <Icon name="Mail" size={16} className="inline mr-2 transition-transform duration-300 hover:scale-110" />
                Check your email for confirmation and next steps
              </p>
              <p className="text-sm text-muted-foreground transition-all duration-300 hover:text-primary">
                <Icon name="Phone" size={16} className="inline mr-2 transition-transform duration-300 hover:scale-110" />
                Need urgent assistance? Call us at +353 1 234 5678
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setSubmitStatus(null)}
              className="mt-6 transition-all duration-300 hover:bg-secondary/10 hover:border-secondary hover:text-secondary hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="proposal-form" className="py-20 bg-surface border-b border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-secondary/20 hover:scale-110 hover:rotate-6">
              <Icon name="FileText" size={20} className="text-secondary" />
            </div>
            <span className="text-sm font-semibold text-secondary uppercase tracking-wider">Custom Proposal Request</span>
          </div>
        </div>
        
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Request a Custom Proposal
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tell us about your project and we'll prepare a detailed proposal with scope, timeline, investment breakdown, and team allocation.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-elevation p-8 transition-all duration-500 hover:shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center transition-colors duration-300 hover:text-secondary">
                <Icon name="User" size={20} className="mr-2 transition-transform duration-300 hover:scale-110" />
                Contact Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  type="text"
                  required
                  value={formData?.firstName}
                  onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                  placeholder="Enter your first name"
                />
                <Input
                  label="Last Name"
                  type="text"
                  required
                  value={formData?.lastName}
                  onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                  placeholder="Enter your last name"
                />
                <Input
                  label="Email Address"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  placeholder="your.email@company.com"
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  required
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="+353 1 234 5678"
                />
              </div>
            </div>

            {/* Company Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
                <Icon name="Building" size={20} className="mr-2" />
                Company Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  type="text"
                  required
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                  placeholder="Your company name"
                />
                <Input
                  label="Job Title"
                  type="text"
                  required
                  value={formData?.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                  placeholder="Your role/position"
                />
                <Select
                  label="Company Size"
                  options={companySizeOptions}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  placeholder="Select company size"
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  placeholder="Select your industry"
                  required
                />
              </div>
            </div>

            {/* Project Details */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
                <Icon name="Briefcase" size={20} className="mr-2" />
                Project Details
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Select
                  label="Project Scope"
                  options={projectScopeOptions}
                  value={formData?.projectScope}
                  onChange={(value) => handleInputChange('projectScope', value)}
                  placeholder="Select primary project scope"
                  required
                />
                <Select
                  label="Project Timeline"
                  options={timelineOptions}
                  value={formData?.projectTimeline}
                  onChange={(value) => handleInputChange('projectTimeline', value)}
                  placeholder="Select desired timeline"
                  required
                />
                <Select
                  label="Budget Range"
                  options={budgetOptions}
                  value={formData?.budget}
                  onChange={(value) => handleInputChange('budget', value)}
                  placeholder="Select budget range"
                  required
                />
              </div>
            </div>

            {/* Detailed Requirements */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
                <Icon name="FileText" size={20} className="mr-2" />
                Detailed Requirements
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Current Challenges <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    required
                    value={formData?.currentChallenges}
                    onChange={(e) => handleInputChange('currentChallenges', e?.target?.value)}
                    placeholder="Describe the key challenges you're facing with your current data strategy, analytics, or governance..."
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Desired Outcomes <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    required
                    value={formData?.desiredOutcomes}
                    onChange={(e) => handleInputChange('desiredOutcomes', e?.target?.value)}
                    placeholder="What specific outcomes or goals do you want to achieve? (e.g., improved decision-making, cost reduction, compliance, etc.)"
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Specific Requirements
                  </label>
                  <textarea
                    value={formData?.specificRequirements}
                    onChange={(e) => handleInputChange('specificRequirements', e?.target?.value)}
                    placeholder="Any specific technical requirements, constraints, or preferences? (e.g., existing tech stack, regulatory requirements, team structure)"
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-primary"
                  />
                </div>
              </div>
            </div>

            {/* GDPR Compliance */}
            <div className="space-y-4 pt-4 border-t border-border">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="gdprConsent"
                  checked={formData?.gdprConsent}
                  onCheckedChange={(checked) => handleInputChange('gdprConsent', checked)}
                  required
                />
                <label htmlFor="gdprConsent" className="text-sm text-muted-foreground cursor-pointer">
                  I consent to Northstar Analytics processing my personal data to prepare and deliver a custom proposal. 
                  I understand my data will be handled in accordance with GDPR regulations and the{' '}
                  <a href="/privacy-policy-cookie-consent-management-gdpr-compliance-center" className="text-secondary hover:underline">
                    Privacy Policy
                  </a>.
                  <span className="text-destructive ml-1">*</span>
                </label>
              </div>
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="marketingConsent"
                  checked={formData?.marketingConsent}
                  onCheckedChange={(checked) => handleInputChange('marketingConsent', checked)}
                />
                <label htmlFor="marketingConsent" className="text-sm text-muted-foreground cursor-pointer">
                  I would like to receive relevant insights, case studies, and updates from Northstar Analytics. 
                  (You can unsubscribe at any time)
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                type="submit"
                variant="default"
                fullWidth
                disabled={isSubmitting || !formData?.gdprConsent}
                iconName={isSubmitting ? "Loader" : "Send"}
                iconPosition="right"
                className="min-h-[48px]"
              >
                {isSubmitting ? 'Submitting Request...' : 'Request Custom Proposal'}
              </Button>
            </div>

            {submitStatus === 'error' && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">Submission Failed</p>
                  <p className="text-sm text-destructive/80 mt-1">
                    We couldn't submit your proposal request. Please try again or contact us directly at contact@northstaranalytics.eu
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Additional Information */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={20} className="text-secondary" />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-1">Confidential & Secure</h4>
              <p className="text-sm text-muted-foreground">
                All information is treated with strict confidentiality and GDPR compliance
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Clock" size={20} className="text-secondary" />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-1">Fast Response</h4>
              <p className="text-sm text-muted-foreground">
                Detailed proposal delivered within 2-3 business days
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Users" size={20} className="text-secondary" />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-1">Expert Team</h4>
              <p className="text-sm text-muted-foreground">
                Proposals prepared by senior data strategy consultants
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomProposalForm;